import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../theme';
import { logger } from '../utils/logger';
import { NotificationsPanel } from './NotificationsPanel';
import { Notification } from '../types';
import { useWebSocket } from '../hooks/useWebSocket';

interface NotificationButtonProps {
  onNotificationClick?: (notification: Notification) => void;
}

// Control global para evitar múltiples instancias
let globalNotificationInitialized = false;
let lastUserId: string | null = null;

export const NotificationButton: React.FC<NotificationButtonProps> = ({
  onNotificationClick
}) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Resetear si cambia el usuario
    if (user?.id !== lastUserId) {
      globalNotificationInitialized = false;
      lastUserId = user?.id || null;
    }
    
    if (globalNotificationInitialized) return; // Evitar ejecución múltiple en StrictMode
    
    if (user?.id) {
      globalNotificationInitialized = true;
      fetchUnreadCount();
      // Actualizar cada 30 segundos
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Solo dependencia del ID, no del objeto completo

  // WebSocket para notificaciones en tiempo real
  const { joinNotifications, leaveNotifications } = useWebSocket(undefined, undefined);

  // Escuchar eventos WebSocket de notificaciones
  useEffect(() => {
    if (user?.id) {
      // Unirse a las notificaciones del usuario via WebSocket
      joinNotifications(user.id);
      
      // Handler unificado para eventos de notificaciones WebSocket
      const handleWebSocketNotification = (event: any) => {
        const data = event.detail || event;
        logger.websocket('Evento de notificación recibido:', data);
        
        if (data.type === 'NEW_NOTIFICATION') {
          // Incrementar el contador cuando llega una nueva notificación
          setUnreadCount(prev => {
            const newCount = prev + 1;
            logger.websocket('Contador de notificaciones incrementado:', { from: prev, to: newCount });
            return newCount;
          });
        } else if (data.type === 'COUNT_UPDATE') {
          // Actualizar el contador cuando se recibe la actualización
          logger.websocket('Contador de notificaciones actualizado:', { count: data.count });
          setUnreadCount(data.count);
        }
      };

      // Agregar listener para eventos WebSocket
      window.addEventListener('websocket-notification', handleWebSocketNotification);
      
      return () => {
        // Salir de las notificaciones cuando se desmonte
        leaveNotifications(user.id);
        window.removeEventListener('websocket-notification', handleWebSocketNotification);
      };
    }
  }, [user?.id, joinNotifications, leaveNotifications]);

  const fetchUnreadCount = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/notifications/unread-count'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      logger.error('Error fetching unread count:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setShowPanel(true);
  };

  const handleClose = () => {
    setShowPanel(false);
    // Refrescar el contador cuando se cierra el panel
    fetchUnreadCount();
  };

  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    setShowPanel(false);
  };

  if (!user) return null;

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          fontSize: '24px',
          cursor: loading ? 'not-allowed' : 'pointer',
          color: theme.colors.text,
          padding: '8px',
          borderRadius: '50%',
          transition: 'all 0.2s ease',
          opacity: loading ? 0.6 : 1
        }}
        title="Notificaciones"
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            backgroundColor: theme.colors.danger,
            color: theme.colors.white,
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: theme.fonts.body,
            fontWeight: theme.fonts.weightBold,
            border: `2px solid ${theme.colors.surface}`
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationsPanel
        isVisible={showPanel}
        onClose={handleClose}
        onNotificationClick={handleNotificationClick}
      />
    </>
  );
}; 